import axios from 'axios';
import type {
  AmhsRequestValidation,
  AmhsStreamActiveResponse,
  AmhsStreamFirstFrame,
  AmhsStreamFrame,
  AmhsStreamRunRequest,
  AmhsStreamStopRequest,
  AmhsStreamStopResponse,
} from '../types/amhsStream';
import {
  getAmhsStreamActiveUrl,
  getAmhsStreamStopUrl,
  getAmhsStreamWebSocketUrl,
  getAmhsValidateUrl,
} from './apiConfig';
import { http } from './http';

export function streamFrameMessage(frame: unknown): string | null {
  if (!frame || typeof frame !== 'object') return null;
  const record = frame as Record<string, unknown>;
  if (typeof record.message === 'string' && record.message.trim()) return record.message;
  const payload = record.payload;
  if (payload && typeof payload === 'object') {
    const nested = (payload as Record<string, unknown>).message;
    if (typeof nested === 'string' && nested.trim()) return nested;
  }
  if (typeof record.detail === 'string' && record.detail.trim()) return record.detail;
  return null;
}

/** Server already dropped the job; local UI should look idle / Start live. */
export function isAmhsStreamMissingMessage(message: string | null | undefined): boolean {
  if (!message) return false;
  return /(?:active\s+)?stream not found/i.test(message);
}

function axiosErrorDetail(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const data = error.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') {
    const detail = (data as { detail?: unknown }).detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (detail && typeof detail === 'object') {
      const nested = (detail as { message?: unknown }).message;
      if (typeof nested === 'string' && nested.trim()) return nested;
    }
  }
  return error.message || null;
}

export function isAmhsStreamMissingError(error: unknown): boolean {
  if (axios.isAxiosError(error) && error.response?.status === 404) return true;
  if (isAmhsStreamMissingMessage(axiosErrorDetail(error))) return true;
  if (error instanceof Error && isAmhsStreamMissingMessage(error.message)) return true;
  if (typeof error === 'string') return isAmhsStreamMissingMessage(error);
  return isAmhsStreamMissingMessage(streamFrameMessage(error));
}

export async function discoverAmhsStream(fabId: string): Promise<AmhsStreamActiveResponse> {
  const response = await http.get<AmhsStreamActiveResponse>(getAmhsStreamActiveUrl(fabId));
  return response.data;
}

/** Check the request the way the API will, without starting a job. */
export async function validateAmhsRunRequest(
  runRequest: AmhsStreamRunRequest
): Promise<AmhsRequestValidation> {
  const response = await http.post<AmhsRequestValidation>(getAmhsValidateUrl(), runRequest);
  const data = response.data;
  return {
    ok: Boolean(data?.ok),
    errors: Array.isArray(data?.errors) ? data.errors : [],
    warnings: Array.isArray(data?.warnings) ? data.warnings : [],
  };
}

export type StopAmhsStreamOptions = {
  /** Abort the request after this long so the UI can report it and retry. */
  timeoutMs?: number;
};

function timeoutSignal(ms: number): AbortSignal {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

/** True when a stream call was aborted by a timeout rather than answered. */
export function isAmhsStreamTimeoutError(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    return error.code === 'ERR_CANCELED' || error.code === 'ECONNABORTED';
  }
  if (error instanceof Error) {
    return error.name === 'TimeoutError' || error.name === 'AbortError';
  }
  return false;
}

export async function stopAmhsStream(
  request: AmhsStreamStopRequest,
  options?: StopAmhsStreamOptions
): Promise<AmhsStreamStopResponse> {
  const response = options?.timeoutMs
    ? await http.post<AmhsStreamStopResponse>(getAmhsStreamStopUrl(), request, {
        signal: timeoutSignal(options.timeoutMs),
      })
    : await http.post<AmhsStreamStopResponse>(getAmhsStreamStopUrl(), request);
  return response.data;
}

export type AmhsStreamSocketHandlers = {
  onFrame: (frame: AmhsStreamFrame) => void;
  onOpen?: () => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (error: Error) => void;
};

export type AmhsStreamSocket = {
  close: () => void;
  readyState: () => number;
};

export function openAmhsStreamSocket(
  firstFrame: AmhsStreamFirstFrame,
  handlers: AmhsStreamSocketHandlers
): AmhsStreamSocket {
  const socket = new WebSocket(getAmhsStreamWebSocketUrl());
  let intentionallyClosed = false;

  socket.addEventListener('open', () => {
    socket.send(JSON.stringify(firstFrame));
    handlers.onOpen?.();
  });

  socket.addEventListener('message', (event) => {
    try {
      const parsed = JSON.parse(String(event.data)) as unknown;
      if (
        !parsed ||
        typeof parsed !== 'object' ||
        typeof (parsed as { type?: unknown }).type !== 'string'
      ) {
        throw new Error('AMHS stream sent a frame without a type.');
      }
      handlers.onFrame(parsed as AmhsStreamFrame);
    } catch (error) {
      handlers.onError?.(
        error instanceof Error ? error : new Error('Could not parse AMHS stream frame.')
      );
    }
  });

  socket.addEventListener('error', () => {
    handlers.onError?.(new Error('AMHS stream WebSocket connection failed.'));
  });

  socket.addEventListener('close', (event) => {
    // Socket lifetime is replica-only. A close never implies that the API job stopped.
    if (!intentionallyClosed) handlers.onClose?.(event);
  });

  return {
    close: () => {
      intentionallyClosed = true;
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close();
      }
    },
    readyState: () => socket.readyState,
  };
}
